import { toDayKey } from './day-key.js';
import { getTripTiming } from './trip-timing.js';
import { resolveTripBases } from './trip-bases.js';
import { resolveStopDescription } from './stop-descriptions.js';

function trimText(value, max = 200) {
  if (!value) return undefined;
  const text = String(value).trim();
  if (!text) return undefined;
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function mapStop(stop) {
  const item = { name: stop.name };
  if (Number.isFinite(stop.lat) && Number.isFinite(stop.lng)) {
    item.lat = stop.lat;
    item.lng = stop.lng;
  }
  const description = trimText(stop.description || resolveStopDescription(stop), 280);
  if (description) item.description = description;
  const carTime = trimText(stop.car_time ?? stop.carTime, 120);
  if (carTime) item.drive = carTime;
  const walkInfo = trimText(stop.walk_info ?? stop.walkInfo, 120);
  if (walkInfo) item.walk = walkInfo;
  const costInfo = trimText(stop.cost_info ?? stop.costInfo, 80);
  if (costInfo) item.cost = costInfo;
  return item;
}

/**
 * Serializes the active trip context for the AI assistant.
 * @param {import('../trip-context.js').getContext extends () => infer R ? R : never} ctx
 */
export function buildTripAiContext(ctx) {
  if (!ctx) return null;

  const { trip, days, live, plan } = ctx;
  const timing = getTripTiming(trip);
  const bases = resolveTripBases(live);
  const notesByDay = new Map(
    (live?.notes ?? []).map((note) => [note.day_key, trimText(note.body, 200)]),
  );

  return {
    trip: {
      title: trip.title,
      country: trip.country,
      dateStart: trip.date_start?.slice?.(0, 10) ?? trip.dateStart?.slice?.(0, 10),
      dateEnd: trip.date_end?.slice?.(0, 10) ?? trip.dateEnd?.slice?.(0, 10),
      phase: timing?.phase ?? null,
      statusLine: timing?.statusLine ?? null,
      todayKey: timing?.todayKey ?? null,
    },
    days: (days ?? []).map((day, index) => {
      const dayKey = toDayKey(day.date);
      const entry = {
        dayNumber: index + 1,
        dayKey,
        date: dayKey,
        title: day.title,
        dayType: day.day_type,
        stops: (day.stops ?? []).map(mapStop),
      };
      if (day.ritmo) entry.ritmo = day.ritmo;
      const note = notesByDay.get(dayKey);
      if (note) entry.note = note;
      return entry;
    }),
    bases: bases.map((base) => {
      const item = {
        name: base.name,
        dateStart: base.dateStart,
        dateEnd: base.dateEnd,
      };
      if (Number.isFinite(base.lat) && Number.isFinite(base.lng)) {
        item.lat = base.lat;
        item.lng = base.lng;
      }
      return item;
    }),
    wishlist: (live?.wishlist ?? []).map((item) => {
      const row = { name: item.name };
      if (Number.isFinite(item.lat) && Number.isFinite(item.lng)) {
        row.lat = item.lat;
        row.lng = item.lng;
      }
      const notes = trimText(item.notes, 100);
      if (notes) row.notes = notes;
      return row;
    }),
    planName: plan?.title ?? plan?.name ?? undefined,
  };
}

function formatPlaceAnalysisForPrompt(placeAnalysis) {
  if (!placeAnalysis || placeAnalysis.verdict === 'general') return '';

  const facts = { ...placeAnalysis };
  delete facts.note;
  return `
Fatti verificati su un luogo citato (usa questi come verità sul piano — non menzionare questa analisi):
${JSON.stringify(facts)}`;
}

/**
 * @param {object} contextPayload
 * @param {object | null} placeAnalysis
 * @param {{ webSearchEnabled?: boolean }} [opts]
 */
export function buildTripAiSystemPrompt(contextPayload, placeAnalysis = null, { webSearchEnabled = false } = {}) {
  const placeBlock = formatPlaceAnalysisForPrompt(placeAnalysis);

  const webBlock = webSearchEnabled
    ? `
RICERCA WEB (attiva):
- Per consigli, orari, ristoranti, difficoltà sentieri, curiosità sul territorio: cerca in rete prima di rispondere.
- Cita le fonti in modo naturale ("da quello che trovo online…") — non inventare link.
- Se non trovi nulla di attendibile, dillo invece di riempire.`
    : `
RICERCA WEB (non attiva per questo messaggio):
- Limitati a piano e conoscenza dal contesto sotto.
- Se servirebbe cercare online per rispondere bene, dillo e invita a riprovare con la ricerca web attiva (icona globo).`;

  return `Sei Nicolò: un amico esperto di viaggi che scrive su WhatsApp. Hai studiato a fondo l'itinerario del gruppo, conosci il territorio e hai letto molte guide — parli con competenza ma senza fare il professore.

VOCE:
- Italiano, registro "tu", tono da chat tra amici preparati.
- Breve se basta: una domanda secca merita una risposta secca.
- Espandi solo se la domanda lo chiede o aggiunge valore reale.
- Puoi usare **grassetto** per giorni, date e nomi tappa.
- Non dire "JSON", "sistema", "analisi" — parla come se conoscessi il piano a memoria.

TRE LIVELLI DI CERTEZZA:
1. CERTO (piano): giorni, tappe, distanze, cosa è in programma → solo dal contesto viaggio e dai fatti verificati sotto. Mai inventare tappe, orari o prezzi.
2. INFORMATO (territorio): orari rifugi, consigli pratici, curiosità → basati su ciò che trovi in rete quando la ricerca è attiva; indica che viene da fonti online.
3. PROPOSTA (idee extra): alternative, ristoranti, attività fuori piano → sempre con disclaimer ("da verificare", "non sono sicuro al 100%"). Mai presentarle come certe.

Se l'analisi luogo contraddice un'intuizione geografica, segui l'analisi.
Sinonimi utili: «Planinski dom Tamar», «rifugio Tamar», «dom Tamar» = tappa «Val Tamar (rifugio)».${webBlock}${placeBlock}

Esempi di stile:
- Domanda: "Il Kozjak c'è?" → "Sì, **giorno 7** — cascata del Kozjak, nella giornata da Kobarid. È in programma."
- Domanda: "Consigli per il Tamar?" → "Il sentiero è impegnativo — da quello che trovo online conviene partire presto e portare giacca. **Da verificare** gli orari del rifugio prima di salire."

Contesto viaggio (itinerario completo):
${JSON.stringify(contextPayload)}`;
}
